<script setup lang="ts">
import { computed, h, type PropType } from 'vue';
import GaiaIconButton from './GaiaIconButton.vue';
import GaiaTooltip from './GaiaTooltip.vue';
import { gaiaGlyphPaths } from '../glyphs';

// Listeners and attrs belong on the button, not on the tooltip wrapping it.
defineOptions({ inheritAttrs: false });

/**
 * The pencil icon button that opens a record for editing — the partner of
 * `GaiaDeleteButton`'s trash in a row's actions, at the same sizes.
 */
const props = defineProps({
  /** Square edge: `xs` for dense rows, `sm` everywhere else, `md` for data-table row actions. */
  size: {
    type: String as PropType<'xs' | 'sm' | 'md'>,
    default: 'sm',
  },
  /** What is edited, e.g. "prompt": composes "Edit prompt" for the title and aria-label. */
  subject: {
    type: String,
    default: '',
  },
  /** Name the action in a GaiaTooltip rather than the native title, as GaiaDeleteButton does. */
  tooltip: {
    type: Boolean,
    default: false,
  },
});

const DIMENSION = { xs: 22, sm: 26, md: 28 } as const;

const dimension = computed(() => DIMENSION[props.size]);
// Half the square's edge, as GaiaDeleteButton draws its glyph.
const glyphSize = computed(() => dimension.value / 2);
const text = computed(() => (props.subject ? `Edit ${props.subject}` : 'Edit'));

// One glyph for both icon branches.
const Glyph = (): ReturnType<typeof h> =>
  h(
    'svg',
    {
      width: glyphSize.value,
      height: glyphSize.value,
      viewBox: '0 0 16 16',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.5,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    },
    [h('path', { d: gaiaGlyphPaths.edit })]
  );
</script>

<template>
  <gaia-tooltip v-if="props.tooltip">
    <template #trigger>
      <gaia-icon-button
        v-bind="$attrs"
        :dimension="dimension"
        size="tiny"
        quaternary
        :aria-label="text"
      >
        <glyph />
      </gaia-icon-button>
    </template>
    {{ text }}
  </gaia-tooltip>
  <gaia-icon-button
    v-else
    v-bind="$attrs"
    :dimension="dimension"
    size="tiny"
    quaternary
    :title="text"
    :aria-label="text"
  >
    <glyph />
  </gaia-icon-button>
</template>
